'use client';

import type { ReactElement } from 'react';
import { useState } from 'react';
import { LogOutIcon, Wallet2Icon } from 'lucide-react';
import { useAccount } from 'wagmi';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBalance } from '@/lib/services/blockchain/service';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { useConnect } from '@/lib/hooks/use-connect';
import { toast } from 'sonner';
import { error } from '@/lib/utils/error';
import { useDisconnect, useAppKit } from '@reown/appkit/react';
import { localeNumber } from '@/lib/utils/format-number';
import { useScopedI18n } from '@/locales/client';

export default function Actions(): ReactElement | null {
  const t = useScopedI18n('header');
  const { address, isConnected, isConnecting, isReconnecting } = useAccount();
  const { balance } = useBalance(address);
  const { disconnect } = useDisconnect();
  const { connect } = useConnect();
  const { open } = useAppKit();

  const [pending, setPending] = useState(false);

  const onConnect = () => toast.promise(async () => connect(), { error });

  if (isConnecting || isReconnecting) return <Skeleton className="w-12 h-10" />;

  if (!isConnected || !address)
    return (
      <div className="w-fit flex flex-row items-center overflow-hidden rounded-md border bg-secondary">
        <Button
          variant="secondary"
          className="w-fit flex flex-row items-center justify-center gap-1.5"
          onClick={onConnect}
        >
          <Wallet2Icon className="w-5 h-5 shrink-0" />
          <p>{t('connect')}</p>
        </Button>
      </div>
    );

  const onDisconnect = async (): Promise<void> => {
    try {
      setPending(true);
      await disconnect();
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="w-fit flex flex-row items-center overflow-hidden rounded-md border bg-secondary">
      <Button
        variant="secondary"
        className="h-9 rounded-none border-0 px-2 sm:px-3 flex flex-row items-center justify-center gap-1.5"
        onClick={() => open()}
      >
        <Wallet2Icon className="w-5 h-5 shrink-0" />
        <div className="w-fit min-w-12 font-bold text-sm">
          {balance.data === undefined ? (
            <Skeleton className="w-12 h-4" />
          ) : (
            <span>
              {localeNumber(balance.data.balance, { maximumFractionDigits: 6 })}{' '}
              {balance.data.chain.nativeCurrency.symbol}
            </span>
          )}
        </div>
      </Button>
      <Button
        variant="secondary"
        className="h-10 w-10 rounded-none border-0 border-l px-0 flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground"
        onClick={onDisconnect}
        disabled={pending}
        title="Disconnect"
      >
        {pending ? <LoadingSpinner className="w-4 h-4" /> : <LogOutIcon className="w-4 h-4" />}
      </Button>
    </div>
  );
}
